/**
 * THE artwork object-name convention, shared by every app that scans student
 * artwork into a Storage bucket: Jarvis desktop, the mobile app, the teacher
 * app, and the scanner app.
 *
 * `{base36 epoch ms}-{6 base36 chars}.{ext}` — nothing else. No date, no
 * student name, no dob. Two reasons:
 *
 * 1. Uniqueness. The old scanner-style name
 *    (`YYYY-MM-DD HH:mm {name} {MMYY}.jpg`) carried only minute resolution,
 *    so a teacher photographing two pieces inside the same minute silently
 *    overwrote the first: a write to an existing object path replaces it,
 *    and there is no "create only" upload. A per-upload id is the only
 *    thing that makes "take as many pictures as you want" true.
 * 2. The date and the student were historical metadata from before the
 *    artwork record carried it. `timeCreated`, `semester`, `classId`, and the
 *    owning student are all on the `student.artwork.{id}` entry now, so the
 *    filename was duplicating them — badly, and with a child's name and birth
 *    month in a bucket object path.
 *
 * The timestamp prefix is kept only so a bucket listing sorts by capture
 * order; nothing parses it. The `_thumb` / `_med` rendition suffixes still
 * attach after the extension-stripped base, so this is compatible with
 * `artworkRenditionHelpers` and with mobile's `-crop-` marker.
 */

/** Characters of randomness after the timestamp. 36^6 ≈ 2.2 billion. */
const RANDOM_LENGTH = 6

const randomSuffix = (): string => {
  let suffix = ""
  // Math.random().toString(36) can be shorter than expected for round values
  // (0.5 -> "0.i"), so draw until there are enough characters.
  while (suffix.length < RANDOM_LENGTH) {
    suffix += Math.random().toString(36).slice(2)
  }
  return suffix.slice(0, RANDOM_LENGTH)
}

/**
 * A fresh artwork file id. Also used as the artwork record's id on the
 * desktop upload path, where the two are deliberately the same value.
 */
export const createArtworkFileId = (): string =>
  `${Date.now().toString(36)}-${randomSuffix()}`

/** `jpeg`/`JPG`/`.png` all normalize to a bare lowercase extension. */
export const normalizeArtworkExtension = (extension: string): string =>
  extension.replace(/^\./, "").toLowerCase() || "jpg"

export const buildArtworkFileName = (
  fileId: string,
  extension = "jpg"
): string => `${fileId}.${normalizeArtworkExtension(extension)}`
